import type { CaseCustomType } from "@/features/customCaseTypes/types";

import { VISAVALUE } from "../../types/visa.type";

export enum PreferenceCategory {
  IR1 = "IR-1",
  IR2 = "IR-2",
  IR3 = "IR-3",
  IR4 = "IR-4",
  IR5 = "IR-5",
  F1 = "F-1",
  F2A = "F-2A",
  F2B = "F-2B",
  F3 = "F-3",
  F4 = "F-4",
  EB1 = "EB-1",
  EB2 = "EB-2",
  EB3 = "EB-3",
  EB4 = "EB-4",
  EB5 = "EB-5"
}

export enum CaseStatus {
  IN_PROGRESS = "In Progress",
  ON_HOLD = "On Hold",
  FILED = "Filed",
  DECISION = "Decision",
  APPROVED = "Approved",
  DENIED = "Denied",
  REOPENED = "Re-opened",
  CLOSED = "Closed"
}

export type ExhibitVerificationStatus =
  | "not_started"
  | "manual_in_progress"
  | "automated_in_progress"
  | "approved"
  | "skipped"
  | "failed";

export type ExhibitVerificationMethod = "manual" | "automated" | "skipped";

export type CaseStrategyMethod = "agent" | "questions" | "skipped";

export interface CreateCaseRequest {
  individual_id: string;
  lawyer_id: string;
  country_of_citizenship?: string;
  visa?: VISAVALUE;
  current_immigration_status?: string;
  immigration_status_expiration?: string; // ISO date string
  priority_date?: string; // ISO date string
  preference_category?: PreferenceCategory;
}

export interface UpdateCaseRequest {
  country_of_citizenship?: string;
  country_of_birth?: string;
  current_immigration_status?: string;
  immigration_status_expiration?: string; // ISO date string
  priority_date?: string; // ISO date string
  preference_category?: PreferenceCategory;
  status?: CaseStatus;
  visa?: VISAVALUE;
  ead_expiration?: string; // ISO date string
  advance_parole_expiration?: string; // ISO date string
  passport_expiration?: string; // ISO date string
  visa_expiration?: string; // ISO date string
  filing_deadline?: string; // ISO date string
  premium_processing?: boolean | null;
  exhibitListFormat?: string;
  exhibit_verification_status?: ExhibitVerificationStatus;
  exhibit_verification_method?: ExhibitVerificationMethod | null;
  exhibit_verification_started_at?: string | null;
  exhibit_verification_completed_at?: string | null;
  exhibit_verification_run_id?: string | null;
  exhibit_verification_accepted_document_ids?: string[];
  exhibit_verification_reviewed_document_ids?: string[];
  case_strategy_method?: CaseStrategyMethod | null;
}

export interface Case {
  case_id: string;
  individual_id: string;
  lawyer_id: string;
  country_of_citizenship: string;
  country_of_birth?: string;
  visa?: VISAVALUE;
  /** A Custom Workflow case's firm custom case type, as named when it started. */
  custom_case_type?: CaseCustomType | null;
  current_immigration_status?: string;
  immigration_status_expiration?: string;
  priority_date?: string;
  preference_category?: PreferenceCategory;
  status: CaseStatus;
  ead_expiration?: string;
  advance_parole_expiration?: string;
  passport_expiration?: string;
  visa_expiration?: string;
  filing_deadline?: string;
  premium_processing?: boolean | null;
  exhibit_verification_status?: ExhibitVerificationStatus;
  exhibit_verification_method?: ExhibitVerificationMethod | null;
  exhibit_verification_started_at?: string | null;
  exhibit_verification_completed_at?: string | null;
  exhibit_verification_run_id?: string | null;
  exhibit_verification_accepted_document_ids?: string[];
  exhibit_verification_reviewed_document_ids?: string[];
  case_strategy_method?: CaseStrategyMethod | null;
  created_at: string;
  updated_at: string;
}

export interface USCISCaseStatus {
  form_type: string;
  history: Array<USCISReceiptHistory>;
  modified_date: string;
  receipt_number: string;
  status_desc_en: string;
  status_desc_es: string;
  status_text_en: string;
  status_text_es: string;
  submitted_date: string;
}

export interface USCISError {
  code: string;
  status?: string;
  message: string;
}

export interface USCISReceipt {
  receipt_id: string;
  case_id: string;
  uscis_receipt_number: string;
  created_at: string;
  updated_at: string | null;
  is_deleted: boolean;
  uscis_case_status: USCISCaseStatus | null;
  uscis_error?: USCISError;
}

export interface USCISReceiptHistory {
  date: string;
  status_en: string;
  status_es: string;
}

export interface AddUSCISReceiptRequest {
  uscis_receipt_number: string;
}
